import { buildTokens, tokensToCssVars } from '../customer/mealpass/storefrontTheme';
import '../customer/mealpass/storefront.css';
import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Trash2, Plus, MapPin } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import MealPackBuilder from './MealPackBuilder';
import { calculateCustomPrice } from '../../utils/pricingCalculator';
import { getTomorrowInTimezone } from '../../utils/timezoneUtils';

const UNIT_OPTIONS = [
  { value: 'oz', label: 'oz' },
  { value: 'pcs', label: 'pcs' },
  { value: 'g', label: 'g' },
  { value: 'kg', label: 'kg' },
  // v5.17 (founder) — lbs + liquid units; canonical unit_conversion tokens.
  { value: 'lb', label: 'lbs' },
  { value: 'ml', label: 'ml' },
  { value: 'l', label: 'L' },
  { value: 'cups', label: 'cups' },
  { value: 'servings', label: 'servings' },
];

// v5.67 - which tenant this dialog fetches its settings and meal items for.
// The page that mounted the dialog has already resolved its tenant (the
// storefront and the catalogue both read ?tenant= / ?subdomain= before the
// host), so an explicit `subdomain` prop wins; without one the dialog reads
// the same two query keys itself, and only then falls back to the host rule
// it always had. Pure (no window access) so the contract can drive it under
// node.
export const resolveModalSubdomain = (explicit, search, hostname) => {
  if (typeof explicit === 'string' && explicit) return explicit;
  const params = new URLSearchParams(search || '');
  const fromQuery = params.get('tenant') || params.get('subdomain');
  if (fromQuery) return fromQuery;
  const host = hostname || '';
  if (host.includes('.plateos.io')) return host.replace('.plateos.io', '');
  // For preview domains and localhost, use super admin tenant
  return 'plateos-super-admin';
};

const AddToCartModal = ({ mealPlan, storeSlug, storeId, preselectedQuantity, enableCustomPricing = true, onClose, brandingHint = null, availableAddons = [], onCheckout = null, brand = null, photo = null, subdomain: subdomainProp = null }) => {
  // v5.66 — the storefront's tokens on the dialog's own node (it portals
  // outside the page shell, so the page's variables never reach it).
  const sfTokens = brandingHint || brand ? buildTokens(brand || brandingHint || {}) : null;
  // the plan's photo: an explicit prop first (the object a card hands over
  // may be a slimmer projection than the one that carried the picture)
  const planPhoto = photo || mealPlan?.image_url || (Array.isArray(mealPlan?.images) ? mealPlan.images[0] : null) || null;
  const { addToCart } = useCart();
  const { formatPrice, getCurrencySymbol } = useCurrency();
  
  const pricing = mealPlan.pricing || {};
  const pricingTiersNested = pricing.tiers || []; // Use 'quantity' field, not 'meals'!
  
  // Use alias if available, otherwise use name
  const displayName = mealPlan.alias || mealPlan.name;
  
  // Detect if this is a custom order
  const isCustomPlan = mealPlan.is_custom_plan || mealPlan.name?.toLowerCase() === 'custom';
  // v4.12 — whether the plan being added IS itself an add-on (don't offer
  // add-ons-within-an-add-on).
  const isAddonPlan = Boolean(mealPlan.is_meal_addon || mealPlan.is_delivery_addon);
  
  // Build available tiers from pricing.tiers array
  const availableTiers = pricingTiersNested
    .filter(tier => tier.price != null && tier.price > 0)
    .map(tier => ({
      meals: tier.quantity || tier.meals || 1, // Use 'quantity' field
      price: tier.price,
      name: tier.name || `${tier.quantity} deliveries`
    }))
    .sort((a, b) => a.meals - b.meals);
  
  // Check if preselected quantity has an exact tier
  const hasExactTier = preselectedQuantity && availableTiers.some(
    t => t.meals === parseInt(preselectedQuantity)
  );
  
  // If preselected quantity doesn't have exact tier, use custom mode (only if custom pricing enabled)
  const shouldUseCustomMode = enableCustomPricing && preselectedQuantity && !hasExactTier && mealPlan.exactPrice;
  
  const [selectedTier, setSelectedTier] = useState(availableTiers[0] || { meals: 5, price: 0, name: '5 deliveries' });
  const oneTimeEnabled = mealPlan.storefront_selling?.one_time_enabled !== false;
  const recurringEnabled = !!mealPlan.storefront_selling?.recurring_enabled && !isCustomPlan && !isAddonPlan;
  const [selectedPurchaseType, setPurchaseType] = useState('one_time');
  // Resolve against current plan settings, including a plan changed while open.
  const purchaseType = recurringEnabled && (!oneTimeEnabled || selectedPurchaseType === 'subscription')
    ? 'subscription' : 'one_time';
  const purchasePrice = price => purchaseType === 'subscription'
    ? Math.round(price * (1 - (mealPlan.storefront_selling?.recurring_discount_percent || 0) / 100) * 100) / 100
    : price;
  
  
  // Initialize delivery days based on available days
  const [deliveryDays, setDeliveryDays] = useState(() => {
    const initial = {
      mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0
    };
    // Set default based on meal plan or will update when global settings load
    return initial;
  });
  
  // NEW FIELDS FOR ENHANCED FUNCTIONALITY
  const [startDate, setStartDate] = useState('');
  const [deliveryInstructions, setDeliveryInstructions] = useState('');
  const [useCustomQuantity, setUseCustomQuantity] = useState(shouldUseCustomMode);
  const [customQuantity, setCustomQuantity] = useState(preselectedQuantity || 5);
  const [customPrice, setCustomPrice] = useState(mealPlan.exactPrice || 0);
  const [globalDeliveryDays, setGlobalDeliveryDays] = useState(null);
  // v5.11 — store pickup address (for pickup-only plans). Loaded from public
  // storefront settings alongside global delivery days.
  const [pickupAddress, setPickupAddress] = useState(null);
  
  // CUSTOM ORDER BUILDER STATE
  const [customMealItems, setCustomMealItems] = useState([]);
  const [availableMealItems, setAvailableMealItems] = useState([]);
  const [loadingMealItems, setLoadingMealItems] = useState(false);
  const [totalDeliveries, setTotalDeliveries] = useState(5); // For custom order pricing tier
  const [useCustomDeliveries, setUseCustomDeliveries] = useState(false);
  const [customDeliveriesCount, setCustomDeliveriesCount] = useState(40);

  // v4.12 — GUIDED ADD-ON WIZARD. The plan config is Step 1; if the store has
  // add-ons and this plan isn't itself an add-on, "Next" reveals Step 2 where
  // each add-on gets its own tier picker (recommended = plan quantity) + a
  // per-day multiplier, capped at the plan's delivery-day count. Replaces the
  // old modal ping-pong (add plan → close → offer modal → open another modal).
  const [step, setStep] = useState('plan'); // 'plan' | 'addons'
  // addonSelections[addon.id] = { days, perDay } (absent = not selected).
  const [addonSelections, setAddonSelections] = useState({});

  // Effective plan delivery-day count (the cap for add-on days).
  const planQuantity = isCustomPlan
    ? totalDeliveries
    : (useCustomQuantity ? Number(customQuantity) : selectedTier.meals);

  const offerAddons = !isAddonPlan && !isCustomPlan && availableAddons.length > 0;

  // v4.14 — the day count is DECOUPLED from tiers (admin add-on modal parity):
  // a single-tier add-on can span ANY number of delivery days; tiers are RATE
  // BREAKS, not day counts. Price = best-fitting tier's per-unit rate × total
  // units, exactly like MealAddonModal.calculatedPrice.
  const addonPricedTiers = (addon) => (
    (addon.pricing?.tiers || [])
      .filter(t => t.price != null && t.price > 0 && (t.quantity || t.meals))
      .map(t => ({ qty: t.quantity || t.meals, price: Number(t.price) }))
      .sort((a, b) => a.qty - b.qty)
  );

  // Highest tier whose qty fits under the total units (bulk rate), else the
  // smallest tier's rate; scaled to the units actually ordered.
  const addonPriceForUnits = (addon, totalUnits) => {
    const tiers = addonPricedTiers(addon);
    if (!tiers.length) return (Number(addon.base_price) || 0) * totalUnits;
    let tier = tiers[0];
    for (const t of tiers) { if (t.qty <= totalUnits) tier = t; }
    return Math.round((tier.price / tier.qty) * totalUnits * 100) / 100;
  };

  const isDayOn = (v) => v === true || v === 1 || v === '1';
  const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

  // The add-on's OWN delivery days (plan override → first meal item), then
  // intersected with the plan's selected days; an empty intersection falls
  // back to the add-on's own days (the backend resolves identically).
  const addonEffectiveDays = (addon) => {
    let own = null;
    if (addon.override_delivery_days && addon.delivery_days_override) {
      own = addon.delivery_days_override;
    } else {
      const mi = addon.meal_items?.[0]?.delivery_days;
      if (mi && Object.values(mi).some(isDayOn)) own = mi;
    }
    if (!own) return deliveryDays; // no own schedule → ride the plan's days
    const inBoth = DAY_KEYS.filter(k => isDayOn(own[k]) && isDayOn(deliveryDays?.[k]));
    const chosen = inBoth.length ? inBoth : DAY_KEYS.filter(k => isDayOn(own[k]));
    return Object.fromEntries(DAY_KEYS.map(k => [k, chosen.includes(k) ? 1 : 0]));
  };

  // An add-on is orderable if it has any priced tier or a base price.
  const addonOrderable = (addon) => addonPricedTiers(addon).length > 0 || Number(addon.base_price) > 0;

  const toggleAddon = (addon) => {
    setAddonSelections(prev => {
      const next = { ...prev };
      if (next[addon.id]) {
        delete next[addon.id];
      } else if (addonOrderable(addon)) {
        // Recommended default: match the plan (one add-on per delivery).
        next[addon.id] = { days: Math.max(1, planQuantity), perDay: 1 };
      }
      return next;
    });
  };

  // Free day-count selection, clamped to 1..planQuantity — the add-on can't
  // span more delivery days than the plan it rides on.
  const setAddonDays = (addon, days) => setAddonSelections(prev => (
    prev[addon.id]
      ? { ...prev, [addon.id]: { ...prev[addon.id], days: Math.max(1, Math.min(planQuantity, days)) } }
      : prev
  ));

  const setAddonPerDay = (addon, perDay) => setAddonSelections(prev => (
    prev[addon.id] ? { ...prev, [addon.id]: { ...prev[addon.id], perDay: Math.max(1, Math.min(3, perDay)) } } : prev
  ));

  // Price for a selected add-on = tier-derived per-unit rate × (days × perDay).
  const addonLinePrice = (addon, sel) => (
    addonPriceForUnits(addon, (sel.days || 1) * (sel.perDay || 1))
  );
  
  // Calculate delivery charge based on meal plan settings
  const getDeliveryCharge = (quantity) => {
    const mode = mealPlan.delivery_charge_mode || 'none';
    if (mode === 'none') return 0;
    if (mode === 'global') {
      return (mealPlan.delivery_charge_per_day || 0) * quantity;
    }
    if (mode === 'per_tier') {
      const tiers = pricingTiersNested;
      const matchedTier = tiers.find(t => t.quantity === quantity);
      if (matchedTier) return matchedTier.delivery_charge || 0;
      // Interpolate from closest tier for custom quantities
      for (const tier of [...tiers].sort((a, b) => (a.quantity || 0) - (b.quantity || 0))) {
        if (tier.delivery_charge != null && tier.quantity > 0) {
          return (tier.delivery_charge / tier.quantity) * quantity;
        }
      }
    }
    return 0;
  };

  // Find free delivery promo message
  const freeDeliveryPromo = useMemo(() => {
    const mode = mealPlan.delivery_charge_mode || 'none';
    if (mode !== 'per_tier') return null;
    const freeTiers = pricingTiersNested.filter(
      t => (t.delivery_charge === 0 || t.delivery_charge === null || t.delivery_charge === undefined) && t.quantity
    );
    if (freeTiers.length === 0) return null;
    const minFreeQty = Math.min(...freeTiers.map(t => t.quantity));
    return `Free delivery on ${minFreeQty}+ deliveries`;
  }, [mealPlan, pricingTiersNested]);
  
  // Prefer the server's next date after applying schedule and cutoff rules.
  const minStartDate = useMemo(() => {
    return mealPlan.next_order_delivery?.date || getTomorrowInTimezone();
  }, [mealPlan.next_order_delivery?.date]);
  
  // Get available delivery days - package level overrides global
  const availableDeliveryDays = useMemo(() => {
    // Check if meal plan has override enabled and override days configured
    if (mealPlan.override_delivery_days && mealPlan.delivery_days_override) {
      console.log('📅 Using package-specific delivery days:', mealPlan.delivery_days_override);
      return mealPlan.delivery_days_override;
    }
    
    // Legacy support: check old field name
    if (mealPlan.delivery_days && typeof mealPlan.delivery_days === 'object') {
      console.log('📅 Using legacy delivery days field:', mealPlan.delivery_days);
      return mealPlan.delivery_days;
    }
    
    // Fall back to global delivery days
    console.log('📅 Using global delivery days:', globalDeliveryDays);
    return globalDeliveryDays;
  }, [mealPlan.override_delivery_days, mealPlan.delivery_days_override, mealPlan.delivery_days, globalDeliveryDays]);
  
  // Get list of days that should be shown (where value is 1)
  const visibleDays = useMemo(() => {
    if (!availableDeliveryDays) return ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    
    return Object.entries(availableDeliveryDays)
      .filter(([day, enabled]) => enabled === 1)
      .map(([day]) => day);
  }, [availableDeliveryDays]);
  
  // Set default start date on mount
  useEffect(() => {
    setStartDate(minStartDate);
  }, [minStartDate]);
  
  // Initialize delivery days when available days are determined
  useEffect(() => {
    if (availableDeliveryDays && visibleDays.length > 0) {
      const newDeliveryDays = {
        mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0
      };
      // Set all visible days to 1 by default (SELECTED)
      visibleDays.forEach(day => {
        newDeliveryDays[day] = 1;
      });
      setDeliveryDays(newDeliveryDays);
      console.log('Initialized delivery days:', newDeliveryDays, 'Visible days:', visibleDays);
    }
  }, [availableDeliveryDays, visibleDays.length]);
  
  // Auto-select tier if quantity is preselected from the selection flow
  useEffect(() => {
    if (preselectedQuantity) {
      const quantity = parseInt(preselectedQuantity);
      const matchingTier = availableTiers.find(t => t.meals === quantity);
      
      if (matchingTier) {
        // Exact tier exists - select it normally
        setSelectedTier(matchingTier);
        setUseCustomQuantity(false);
        console.log('✅ Auto-selected tier:', matchingTier);
      } else if (mealPlan.exactPrice) {
        // No exact tier - enable custom mode with calculated price
        setUseCustomQuantity(true);
        setCustomQuantity(quantity);
        setCustomPrice(mealPlan.exactPrice);
        console.log('✅ Auto-enabled custom mode:', quantity, 'deliveries @', mealPlan.exactPrice);
      }
    }
  }, [preselectedQuantity]); // Remove availableTiers dependency to prevent infinite loop


  
  // Fetch global delivery days settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Get subdomain - the mounting page's tenant, then ?tenant=, then the host (v5.67)
        const hostname = window.location.hostname;
        const subdomain = resolveModalSubdomain(subdomainProp, window.location.search, hostname);
        
        // Check if we're in preview mode
        const isPreview = hostname.includes('preview.emergentagent.com') || hostname.includes('localhost');
        const settingsEndpoint = isPreview ? 'preview/settings' : 'settings';

        // v5.11 — pass store_slug so the per-store pickup address resolves.
        const storeParam = storeSlug ? `?store_slug=${encodeURIComponent(storeSlug)}` : '';

        // Use public storefront settings endpoint (no auth required)
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/storefront/${settingsEndpoint}/${subdomain}${storeParam}`
        );
        if (response.ok) {
          const data = await response.json();
          const globalDays = data.global_delivery_days || {
            mon: 1, tue: 1, wed: 1, thu: 1, fri: 1, sat: 0, sun: 0
          };
          setGlobalDeliveryDays(globalDays);
          setPickupAddress(data.pickup_address || null);
          console.log('Loaded global delivery days:', globalDays);
        } else {
          // Fallback to default
          setGlobalDeliveryDays({
            mon: 1, tue: 1, wed: 1, thu: 1, fri: 1, sat: 0, sun: 0
          });
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        // Fallback to default
        setGlobalDeliveryDays({
          mon: 1, tue: 1, wed: 1, thu: 1, fri: 1, sat: 0, sun: 0
        });
      }
    };
    fetchSettings();
  }, [storeSlug, subdomainProp]);

  // Fetch available meal items for custom orders
  useEffect(() => {
    if (isCustomPlan) {
      fetchMealItems();
    }
  }, [isCustomPlan, subdomainProp]);
  
  const fetchMealItems = async () => {
    setLoadingMealItems(true);
    try {
      // Get subdomain - the mounting page's tenant, then ?tenant=, then the host (v5.67)
      const hostname = window.location.hostname;
      const subdomain = resolveModalSubdomain(subdomainProp, window.location.search, hostname);
      
      // Build query parameters for store filtering
      const storeParam = storeSlug ? `?store_slug=${storeSlug}` : '';
      
      // Check if preview mode
      const isPreview = hostname.includes('preview.emergentagent.com') || hostname.includes('localhost');
      const endpoint = isPreview ? 'preview/meal-items' : 'meal-items';
      
      // Use public storefront meal items endpoint
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/storefront/${endpoint}/${subdomain}${storeParam}`
      );
      if (response.ok) {
        const data = await response.json();
        setAvailableMealItems(data.meal_items || []);
        console.log(`✅ Fetched ${data.meal_items?.length || 0} meal items for ${storeSlug || 'all stores'} (subdomain: ${subdomain})`);
      } else {
        console.error('Failed to fetch meal items:', response.status);
        // Log the error for debugging
        const errorData = await response.text();
        console.error('Error response:', errorData);
      }
    } catch (error) {
      console.error('Error fetching meal items:', error);
    } finally {
      setLoadingMealItems(false);
    }
  };

  // Calculate custom price if using custom quantity
  const calculateCustomPrice = (plan, qty) => {
    const tiers = pricing.tiers || [];
    if (tiers.length === 0) return 0;
    
    // Find highest tier and calculate price per delivery
    const maxTier = tiers.reduce((max, t) => 
      t.quantity > max.quantity ? t : max
    );
    
    if (maxTier.quantity > 0) {
      return (maxTier.price / maxTier.quantity) * qty;
    }
    return 0;
  };
  
  // Custom Order: Add new meal item
  const handleAddCustomItem = () => {
    // Initialize delivery days with global visible days (all selected by default)
    const initialDays = {
      mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0
    };
    visibleDays.forEach(day => {
      initialDays[day] = 1; // All visible days selected by default
    });
    
    const newItem = {
      id: `item-${Date.now()}`,
      item_id: null,
      name: '',
      quantity: 1,
      unit: 'pcs',
      delivery_days: initialDays
    };
    setCustomMealItems([...customMealItems, newItem]);
  };
  
  // Custom Order: Select meal item from dropdown
  const handleSelectMealItem = (itemId, mealItem) => {
    // Calculate price based on total deliveries
    const itemPrice = calculateItemPrice(mealItem, totalDeliveries);
    
    setCustomMealItems(prev => prev.map(item =>
      item.id === itemId ? {
        ...item,
        item_id: mealItem.id,
        name: mealItem.name,
        unit: mealItem.unit || mealItem.default_unit || 'pcs',
        price: itemPrice,
        _mealItem: mealItem
      } : item
    ));
  };
  
  // Calculate item price based on total deliveries and price tiers
  const calculateItemPrice = (mealItem, deliveries) => {
    const tiers = mealItem.price_tiers || [];
    
    if (tiers.length === 0) {
      const basePrice = mealItem.base_price || 0;
      if (basePrice === 0) {
        console.error(`⚠️ Meal item "${mealItem.name}" has no pricing configured (no tiers, base_price = 0)`);
      }
      return basePrice;
    }
    
    // Sort tiers by quantity
    const sortedTiers = [...tiers].sort((a, b) => (a.quantity || 0) - (b.quantity || 0));
    
    // Find applicable tier (highest tier where deliveries >= tier.quantity)
    let applicableTier = sortedTiers[0];
    for (const tier of sortedTiers) {
      if (deliveries >= (tier.quantity || 0)) {
        applicableTier = tier;
      }
    }
    
    // CRITICAL FIX: tier.price is TOTAL for all deliveries, not per-delivery
    // Return per-delivery price = total price / quantity
    // Example: {quantity: 5, price: 22.50} → $22.50 / 5 = $4.50 per delivery
    const perDeliveryPrice = applicableTier.price / (applicableTier.quantity || 1);
    
    console.log(`📊 Pricing for "${mealItem.name}":`, {
      deliveries,
      selectedTier: `${applicableTier.quantity} deliveries @ ${formatPrice(applicableTier.price)}`,
      perDeliveryPrice: formatPrice(perDeliveryPrice)
    });
    
    return perDeliveryPrice;
  };
  
  // Calculate actual delivery days for an item based on:
  // 1. Package delivery days (global)
  // 2. Item-specific delivery days
  // 3. Total deliveries
  const calculateItemDeliveryDays = (itemDeliveryDays, packageDeliveryDays, totalDeliveries) => {
    if (!itemDeliveryDays) return totalDeliveries;
    
    // Count how many days per week are enabled for package
    const packageDaysPerWeek = Object.values(packageDeliveryDays).filter(v => v === 1).length;
    
    // Count how many days per week are enabled for this item (must also be enabled in package)
    const itemDaysPerWeek = Object.entries(itemDeliveryDays)
      .filter(([day, enabled]) => enabled === 1 && packageDeliveryDays[day] === 1)
      .length;
    
    if (packageDaysPerWeek === 0) return 0;
    
    // Calculate ratio of item days to package days
    const ratio = itemDaysPerWeek / packageDaysPerWeek;
    
    // Actual item deliveries = total deliveries * ratio
    return Math.round(totalDeliveries * ratio);
  };
  
  // Calculate item subtotal with delivery day adjustment
  const calculateItemSubtotal = (item) => {
    if (!item.name || !item.price) return 0;
    
    const actualDeliveries = calculateItemDeliveryDays(item.delivery_days, deliveryDays, totalDeliveries);
    return item.price * actualDeliveries * item.quantity;
  };
  
  // Recalculate all item prices when total deliveries changes
  useEffect(() => {
    if (isCustomPlan && customMealItems.length > 0) {
      setCustomMealItems(prev => prev.map(item => {
        if (!item._mealItem) return item;
        return {
          ...item,
          price: calculateItemPrice(item._mealItem, totalDeliveries)
        };
      }));
    }
  }, [totalDeliveries, isCustomPlan]);
  
  // Custom Order: Update item field
  const handleUpdateCustomItem = (itemId, field, value) => {
    setCustomMealItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  };
  
  // Custom Order: Remove item
  const handleRemoveCustomItem = (itemId) => {
    setCustomMealItems(prev => prev.filter(item => item.id !== itemId));
  };
  
  // Custom Order: Toggle delivery day for specific item
  const handleToggleItemDay = (itemId, day) => {
    setCustomMealItems(prev => prev.map(item => {
      if (item.id !== itemId) return item;
      return {
        ...item,
        delivery_days: {
          ...item.delivery_days,
          [day]: item.delivery_days[day] ? 0 : 1
        }
      };
    }));
  };

  // Validate the plan config (start date on a delivery day, custom-order
  // items). Returns true when the plan is addable. Used both to gate the
  // "Next: Add-ons" step and inside addPlanToCart.
  const validatePlan = () => {
    if (!oneTimeEnabled && !recurringEnabled) {
      alert('This plan is not currently available to purchase.');
      return false;
    }
    if (!startDate) {
      alert('Please select a start date');
      return false;
    }
    // v5.11 — pickup-only plans have no delivery-day-of-week schedule, so skip
    // the "start date must land on a delivery day" validation entirely.
    if (!mealPlan.pickup_only) {
      const date = new Date(startDate + 'T00:00:00');
      const dayNames = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
      const selectedDayKey = dayNames[date.getDay()];
      const isDeliveryDay = deliveryDays[selectedDayKey] === 1;
      if (!isDeliveryDay) {
        const activeDeliveryDays = Object.entries(deliveryDays)
          .filter(([_, active]) => active === 1)
          .map(([day]) => day.charAt(0).toUpperCase() + day.slice(1));
        alert(`Please select a start date that falls on a delivery day: ${activeDeliveryDays.join(', ')}`);
        return false;
      }
    }
    if (isCustomPlan && customMealItems.length === 0) {
      alert('Please add at least one meal item to your custom order');
      return false;
    }
    if (isCustomPlan) {
      const invalidItems = customMealItems.filter(item => !item.name);
      if (invalidItems.length > 0) {
        alert('Please select a meal item for all entries');
        return false;
      }
    }
    return true;
  };
  // Alias for the "Next" gate (reads naturally at the call site).
  const addPlanToCartValid = validatePlan;

  // Validate + add the PLAN to the cart. Returns true on success. Threads an
  // optional cartGroupId so wizard-selected add-ons can be linked to THIS plan
  // at checkout (v4.12) instead of the old server-side date-window heuristic.
  const addPlanToCart = (cartGroupId = null) => {
    if (!validatePlan()) return false;

    // Calculate total price for custom order
    let finalPrice = 0;
    let cleanedCustomItems = null;

    if (isCustomPlan) {
      finalPrice = customMealItems.reduce((sum, item) => sum + calculateItemSubtotal(item), 0);
      if (finalPrice <= 0) {
        alert('Cannot checkout: One or more items have invalid pricing ($0). Please remove items with no pricing or contact support.');
        return false;
      }
      cleanedCustomItems = customMealItems.map(item => ({
        id: item.id,
        item_id: item.item_id,
        name: item.name,
        quantity: item.quantity,
        unit: item.unit,
        price: item.price,
        delivery_days: item.delivery_days
      }));
    } else {
      finalPrice = useCustomQuantity
        ? calculateCustomPrice(mealPlan, customQuantity)
        : selectedTier.price;
    }

    const effectiveQuantity = isCustomPlan ? totalDeliveries : (useCustomQuantity ? customQuantity : selectedTier.meals);
    const deliveryCharge = isCustomPlan ? 0 : getDeliveryCharge(effectiveQuantity);
    const effectiveStoreId = storeId || (mealPlan.store_ids && mealPlan.store_ids.length > 0 ? mealPlan.store_ids[0] : null);

    addToCart(mealPlan, {
      quantity: isCustomPlan ? totalDeliveries : (useCustomQuantity ? customQuantity : selectedTier.meals),
      purchaseType,
      recurring: purchaseType === 'subscription',
      // v5.11 — pickup-only plans carry no delivery-day schedule.
      deliveryDays: mealPlan.pickup_only ? null : deliveryDays,
      price: purchasePrice(finalPrice),
      deliveryCharge,
      startDate,
      deliveryInstructions,
      customMealItems: cleanedCustomItems,
      isCustomOrder: isCustomPlan,
      storeSlug: storeSlug,
      storeId: effectiveStoreId,
      isAddon: mealPlan.is_meal_addon || mealPlan.is_delivery_addon || false,
      cartGroupId,  // v4.12 — link key shared with this plan's add-ons
    });
    return true;
  };

  // Finish the wizard: add the plan + every selected add-on under one group
  // id, then go straight to checkout (falls back to onClose when the mount
  // point didn't supply an onCheckout — e.g. the plans catalogue).
  const handleFinish = () => {
    const cartGroupId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `grp_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
    if (!addPlanToCart(cartGroupId)) return;

    const effectiveStoreId = storeId || (mealPlan.store_ids && mealPlan.store_ids.length > 0 ? mealPlan.store_ids[0] : null);
    Object.entries(addonSelections).forEach(([addonId, sel]) => {
      const addon = availableAddons.find(a => a.id === addonId);
      if (!addon) return;
      const days = sel.days;
      const perDay = sel.perDay || 1;
      addToCart(addon, {
        // meal_quantity = total units (days × perDay); deliveries_per_day lets
        // the backend end-date math span exactly `days` delivery days.
        quantity: days * perDay,
        deliveriesPerDay: perDay,
        purchaseType,
        // v4.14 — the add-on's OWN delivery days (∩ the plan's selection),
        // not a blind copy of the plan schedule.
        deliveryDays: addonEffectiveDays(addon),
        price: addonLinePrice(addon, sel),
        deliveryCharge: 0,
        startDate,
        deliveryInstructions: '',
        isCustomOrder: false,
        storeSlug: storeSlug,
        storeId: addon.store_ids?.[0] || effectiveStoreId,
        isAddon: true,
        cartGroupId,             // link to the plan added above
      });
    });

    if (typeof onCheckout === 'function') onCheckout();
    else onClose();
  };

  // Back-compat entry point (no add-ons): add the plan, then checkout/close.
  const handleAddToCart = () => {
    if (!addPlanToCart(null)) return;
    if (typeof onCheckout === 'function') onCheckout();
    else onClose();
  };

  const dayLabels = {
    mon: 'Mon',
    tue: 'Tue',
    wed: 'Wed',
    thu: 'Thu',
    fri: 'Fri',
    sat: 'Sat',
    sun: 'Sun'
  };

  // Cinematic branding: derive CSS variable overrides from the storefront's brand
  // palette (passed via brandingHint). When set, we inject brand color values that
  // the ".storefront-brand-scope" CSS rules read to re-theme legacy teal utility
  // classes without touching the 1000+ lines of business logic below.
  const brandPrimary = brandingHint?.primary_color || null;
  const brandAccent = brandingHint?.accent_color || null;
  const brandStyle = brandPrimary
    ? {
        // Expose brand colors as CSS variables so the CSS block below can theme
        // all descendant teal-* / slate-* utility classes.
        '--brand-primary': brandPrimary,
        '--brand-primary-tint': `${brandPrimary}18`,   // ~10% opacity
        '--brand-primary-soft': `${brandPrimary}30`,   // ~19% opacity
        '--brand-primary-medium': `${brandPrimary}80`, // ~50%
        '--brand-accent': brandAccent || brandPrimary,
      }
    : {};

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className={`max-w-2xl max-h-[90vh] overflow-y-auto ${brandPrimary ? 'storefront-brand-scope sf-root p-0 border-0' : ''}`}
        style={brandPrimary ? { ...brandStyle, ...(sfTokens ? tokensToCssVars(sfTokens) : {}), borderRadius: 'calc(var(--sf-radius, 1.25rem) * 1.2)' } : brandStyle}
      >
        {brandPrimary && (
          <style>{`
            /* Scoped brand theming — re-colors legacy teal + slate utilities
               inside this modal to match the merchant's storefront palette. */
            .storefront-brand-scope { font-family: var(--sf-body, Inter, ui-sans-serif, system-ui, sans-serif); }
            .storefront-brand-scope [class*="text-teal"] { color: var(--brand-primary) !important; }
            .storefront-brand-scope [class*="bg-teal-50"] { background-color: var(--brand-primary-tint) !important; }
            .storefront-brand-scope [class*="bg-teal-100"] { background-color: var(--brand-primary-soft) !important; }
            .storefront-brand-scope [class*="bg-teal-500"],
            .storefront-brand-scope [class*="bg-teal-600"],
            .storefront-brand-scope [class*="bg-teal-700"] { background-color: var(--brand-primary) !important; color: #fff !important; }
            .storefront-brand-scope [class*="hover:bg-teal-"] { --tw-bg-opacity: 1; }
            .storefront-brand-scope [class*="hover:bg-teal-"]:hover { background-color: var(--brand-primary) !important; color: #fff !important; opacity: 0.9; }
            .storefront-brand-scope [class*="border-teal"] { border-color: var(--brand-primary) !important; }
            .storefront-brand-scope [class*="ring-teal"] { --tw-ring-color: var(--brand-primary) !important; }
            /* Primary CTA — rounded pill instead of shadcn default */
            .storefront-brand-scope [data-slot="button"],
            .storefront-brand-scope button.bg-teal-600,
            .storefront-brand-scope button.bg-teal-500 {
              border-radius: 9999px !important;
              letter-spacing: 0.15em;
              font-weight: 500;
            }
            .storefront-brand-scope .dialog-header-serif {
              font-family: var(--sf-heading, 'Playfair Display', ui-serif, Georgia, serif) !important;
              font-weight: 600 !important;
              font-size: clamp(1.5rem, 3vw, 2rem) !important;
              line-height: 1.1 !important;
            }
            .storefront-brand-scope .cinematic-header-stripe {
              background: var(--brand-primary);
              height: 6rem;
              display: flex;
              align-items: center;
              justify-content: center;
              margin: 0 0 1.5rem 0;
              border-radius: calc(var(--sf-radius, 1.25rem) * 1.2) calc(var(--sf-radius, 1.25rem) * 1.2) 0 0;
            }
            .storefront-brand-scope .cinematic-header-photo {
              height: 11rem;
              margin: 0 0 1.5rem 0;
              overflow: hidden;
              position: relative;
              border-radius: calc(var(--sf-radius, 1.25rem) * 1.2) calc(var(--sf-radius, 1.25rem) * 1.2) 0 0;
            }
            .storefront-brand-scope .cinematic-header-photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
            .storefront-brand-scope .cinematic-header-photo::after {
              content: ""; position: absolute; inset: 0;
              background: linear-gradient(to top, rgba(0,0,0,0.45), transparent 60%);
            }
          `}</style>
        )}

        {brandPrimary && (planPhoto ? (
          <div className="cinematic-header-photo" data-testid="addtocart-plan-photo">
            <img src={planPhoto} alt="" />
          </div>
        ) : (
          <div className="cinematic-header-stripe">
            <Plus className="w-10 h-10 text-white/40" strokeWidth={1.2} />
          </div>
        ))}

        <DialogHeader className={brandPrimary ? 'px-6 pt-2 pb-0' : ''}>
          <DialogTitle className={brandPrimary ? 'dialog-header-serif' : ''}>
            {displayName}
          </DialogTitle>
          {brandPrimary && mealPlan.description && (
            <p className="text-sm text-neutral-600 mt-2 leading-relaxed">
              {mealPlan.description}
            </p>
          )}
        </DialogHeader>

        <div className={`space-y-6 ${brandPrimary ? 'px-6 pb-6' : ''}`}>
          {/* v4.12 — wizard step indicator (only when add-ons are offered). */}
          {offerAddons && (
            <div className="flex items-center gap-2 text-xs font-medium" data-testid="addtocart-step-indicator">
              <span className={step === 'plan' ? 'text-teal-700' : 'text-slate-400'}>1. Your plan</span>
              <span className="text-slate-300">→</span>
              <span className={step === 'addons' ? 'text-teal-700' : 'text-slate-400'}>2. Add-ons (optional)</span>
            </div>
          )}

          {/* ─────────── STEP 1: PLAN CONFIG ─────────── */}
          {step === 'plan' && (<>
          {recurringEnabled && <fieldset className="rounded-xl border p-4">
            <legend className="px-2 font-semibold">Purchase options</legend>
            {oneTimeEnabled && <label className="flex gap-2 py-2"><input type="radio" name="purchase-mode" checked={purchaseType === 'one_time'} onChange={() => setPurchaseType('one_time')} />One-time purchase</label>}
            <label className="flex gap-2 py-2"><input type="radio" name="purchase-mode" checked={purchaseType === 'subscription'} onChange={() => setPurchaseType('subscription')} />Subscribe & save {formatPrice(Math.round((useCustomQuantity ? calculateCustomPrice(mealPlan, customQuantity) : selectedTier.price) * (mealPlan.storefront_selling?.recurring_discount_percent || 0)) / 100)} per plan</label>
            {purchaseType === 'subscription' && <p className="text-sm text-slate-600">Renews after your remaining deliveries are used. Skips and pauses will delay renewal. Review the total and recurring payment terms at checkout.</p>}
          </fieldset>}

          {/* CUSTOM ORDER BUILDER */}
          {isCustomPlan && (
            <div className="border-2 border-teal-200 rounded-lg p-4 bg-teal-50 space-y-4">
              {/* Total Deliveries Selection */}
              <div className="bg-white p-4 rounded-lg border border-teal-300">
                <Label className="mb-2 block font-semibold text-teal-900">
                  Total Number of Deliveries
                </Label>
                <p className="text-xs text-slate-600 mb-3">
                  Select how many total deliveries you want. This affects the per-item pricing.
                </p>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {[5, 10, 20, 30].map((qty) => (
                    <button
                      key={qty}
                      type="button"
                      onClick={() => {
                        setTotalDeliveries(qty);
                        setUseCustomDeliveries(false);
                      }}
                      className={`p-2 border-2 rounded-lg text-sm font-medium transition-colors ${
                        totalDeliveries === qty && !useCustomDeliveries
                          ? 'border-teal-500 bg-teal-100 text-teal-900'
                          : 'border-slate-200 hover:border-slate-300 text-slate-700'
                      }`}
                    >
                      {qty}
                    </button>
                  ))}
                </div>
                
                {/* Custom Deliveries Input */}
                <div className="border-t pt-3">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={useCustomDeliveries}
                      onCheckedChange={(checked) => {
                        setUseCustomDeliveries(checked);
                        if (checked) {
                          setTotalDeliveries(customDeliveriesCount);
                        } else {
                          setTotalDeliveries(5);
                        }
                      }}
                      id="custom-deliveries"
                    />
                    <Label htmlFor="custom-deliveries" className="text-sm cursor-pointer flex-1">
                      Custom number of deliveries
                    </Label>
                    {useCustomDeliveries && (
                      <Input
                        type="number"
                        value={customDeliveriesCount}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 1;
                          setCustomDeliveriesCount(val);
                          setTotalDeliveries(val);
                        }}
                        min={1}
                        max={100}
                        className="w-24"
                        placeholder="Enter..."
                      />
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <Label className="text-lg font-semibold text-teal-900">Build Your Custom Meal</Label>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAddCustomItem}
                  className="bg-teal-600 hover:bg-teal-700"
                  disabled={loadingMealItems || availableMealItems.length === 0}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Item
                </Button>
              </div>
              
              {loadingMealItems ? (
                <p className="text-sm text-slate-600">Loading meal items...</p>
              ) : availableMealItems.length === 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800 font-medium mb-2">No meal items available</p>
                  <p className="text-xs text-yellow-700">
                    This store doesn&apos;t have any meal items with pricing configured. 
                    Please contact the store administrator to add meal items before creating custom orders.
                  </p>
                </div>
              ) : customMealItems.length === 0 ? (
                <p className="text-sm text-slate-600">Click &quot;Add Item&quot; to start building your custom meal ({availableMealItems.length} items available)</p>
              ) : (
                <div className="space-y-4">
                  {customMealItems.map((item, idx) => (
                    <div key={item.id} className="bg-white p-4 rounded-lg border border-slate-200">
                      <div className="flex items-start justify-between mb-3">
                        <Label className="text-sm font-medium">Item {idx + 1}</Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveCustomItem(item.id)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      {/* Meal Item Selector */}
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs mb-1">Select Item</Label>
                          <Select
                            value={item.item_id || ''}
                            onValueChange={(value) => {
                              const selectedItem = availableMealItems.find(mi => mi.id === value);
                              if (selectedItem) handleSelectMealItem(item.id, selectedItem);
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Choose a meal item..." />
                            </SelectTrigger>
                            <SelectContent>
                              {availableMealItems.map((mealItem) => (
                                <SelectItem key={mealItem.id} value={mealItem.id}>
                                  {mealItem.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Quantity and Unit */}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs mb-1">Quantity per Delivery</Label>
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleUpdateCustomItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                              min={1}
                              step={1}
                            />
                          </div>
                          <div>
                            <Label className="text-xs mb-1">Unit</Label>
                            <div className="h-10 flex items-center px-3 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-700">
                              {item.unit || 'pcs'}
                            </div>
                          </div>
                        </div>
                        
                        {/* Price Display */}
                        {item.name && item.price != null && (
                          <div className="bg-teal-50 border border-teal-200 rounded p-2">
                            <p className="text-xs text-teal-700">
                              Price: <span className="font-semibold">{formatPrice(item.price)}</span> per delivery
                            </p>
                            {(() => {
                              const actualDeliveries = calculateItemDeliveryDays(item.delivery_days, deliveryDays, totalDeliveries);
                              const itemTotal = item.price * actualDeliveries * item.quantity;
                              return (
                                <>
                                  <p className="text-xs text-teal-600 mt-1">
                                    Actual deliveries: {actualDeliveries} (based on selected days)
                                  </p>
                                  <p className="text-xs text-teal-600 font-semibold">
                                    Item total: {formatPrice(itemTotal)}
                                  </p>
                                </>
                              );
                            })()}
                          </div>
                        )}
                        
                        {/* Per-Item Delivery Days */}
                        <div>
                          <Label className="text-xs mb-2 block">Delivery Days for This Item</Label>
                          <div className={`grid gap-1`} style={{ gridTemplateColumns: `repeat(${visibleDays.length}, minmax(0, 1fr))` }}>
                            {visibleDays.map((day) => {
                              const active = item.delivery_days[day];
                              return (
                                <button
                                  key={day}
                                  type="button"
                                  onClick={() => handleToggleItemDay(item.id, day)}
                                  className={`p-2 text-xs font-medium rounded transition-colors ${
                                    active 
                                      ? 'bg-teal-500 text-white' 
                                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                  }`}
                                >
                                  {dayLabels[day]}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Total Price Summary */}
              {customMealItems.length > 0 && (
                <div className="bg-white border-2 border-teal-400 rounded-lg p-4 mt-4">
                  <div className="space-y-2">
                    {customMealItems.map((item, idx) => {
                      if (!item.name || !item.price) return null;
                      const actualDeliveries = calculateItemDeliveryDays(item.delivery_days, deliveryDays, totalDeliveries);
                      const itemTotal = calculateItemSubtotal(item);
                      return (
                        <div key={item.id} className="flex justify-between text-sm">
                          <span className="text-slate-600">
                            {item.name} ({item.quantity} {item.unit} × {actualDeliveries} deliveries)
                          </span>
                          <span className="font-medium text-slate-900">
                            {formatPrice(itemTotal)}
                          </span>
                        </div>
                      );
                    })}
                    <div className="border-t pt-2 mt-2 flex justify-between">
                      <span className="font-bold text-lg text-teal-900">Total Price:</span>
                      <span className="font-bold text-lg text-teal-900">
                        {formatPrice(customMealItems.reduce((sum, item) => sum + calculateItemSubtotal(item), 0))}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Meal Quantity Selection - Hide for custom orders, hide when tier is preselected and custom pricing OFF */}
          {!isCustomPlan && availableTiers.length > 0 && !useCustomQuantity && (!preselectedQuantity || enableCustomPricing) && (
            <div>
              <Label className="mb-2 block">Number of Deliveries</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                {availableTiers.map((tier) => {
                  const tierDC = getDeliveryCharge(tier.meals);
                  return (
                  <button
                    key={tier.meals}
                    onClick={() => setSelectedTier(tier)}
                    className={`p-3 border-2 rounded-lg transition-colors ${
                      selectedTier.meals === tier.meals
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <p className="font-semibold text-sm">{tier.name}</p>
                    <p className="text-sm text-slate-600">{formatPrice(tier.price)}</p>
                    {tierDC > 0 && (
                      <p className="text-xs text-slate-400 mt-0.5">+ {formatPrice(tierDC)} delivery</p>
                    )}
                    {tierDC === 0 && mealPlan.delivery_charge_mode !== 'none' && (
                      <p className="text-xs text-emerald-600 font-medium mt-0.5">Free delivery</p>
                    )}
                  </button>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Show selected tier when tier buttons are hidden (custom pricing OFF + preselected) */}
          {!isCustomPlan && !useCustomQuantity && preselectedQuantity && !enableCustomPricing && (
            <div className="border-2 border-slate-200 bg-slate-50 rounded-lg p-4">
              <Label className="mb-2 block">Selected Package</Label>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-900">{selectedTier.name}</p>
                  <p className="text-sm text-slate-600">{selectedTier.meals} deliveries</p>
                </div>
                <p className="text-2xl font-bold text-teal-600">{formatPrice(selectedTier.price)}</p>
              </div>
            </div>
          )}
          
          {/* Custom Quantity Option - Hide for custom orders, hide when custom pricing is disabled */}
          {!isCustomPlan && enableCustomPricing && pricing.custom_enabled && (
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center space-x-2">
                <Switch 
                  checked={useCustomQuantity}
                  onCheckedChange={setUseCustomQuantity}
                  id="custom-quantity"
                  disabled={shouldUseCustomMode}
                />
                <Label htmlFor="custom-quantity" className="cursor-pointer">
                  Use custom number of deliveries {shouldUseCustomMode && <span className="text-xs text-teal-600">(Auto-enabled)</span>}
                </Label>
              </div>
              
              {useCustomQuantity && (
                <div className="space-y-3">
                  <div>
                    <Label className="mb-2 block">Number of Deliveries</Label>
                    <Input 
                      type="number"
                      value={customQuantity}
                      onChange={(e) => setCustomQuantity(parseInt(e.target.value) || 1)}
                      min={1}
                      max={100}
                      className="w-32"
                      disabled={shouldUseCustomMode}
                    />
                  </div>
                  
                  {shouldUseCustomMode && (
                    <div>
                      <Label className="mb-2 block">Price (Calculated)</Label>
                      <Input 
                        type="number"
                        step="0.01"
                        value={customPrice}
                        onChange={(e) => setCustomPrice(parseFloat(e.target.value) || 0)}
                        className="w-32"
                      />
                      <p className="text-xs text-teal-600 mt-1">
                        * Price calculated based on available tiers
                      </p>
                    </div>
                  )}
                  
                  {!shouldUseCustomMode && (
                    <p className="text-xs text-slate-500">
                      Estimated price: {formatPrice(calculateCustomPrice(mealPlan, customQuantity))}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Show custom mode info when auto-enabled */}
          {shouldUseCustomMode && !pricing.custom_enabled && (
            <div className="border-2 border-teal-200 bg-teal-50 rounded-lg p-4">
              <p className="text-sm text-teal-800">
                <strong>Custom Pricing:</strong> This package doesn't have a standard tier for {preselectedQuantity} deliveries. 
                Price of {formatPrice(mealPlan.exactPrice)} is calculated based on available pricing tiers.
              </p>
              <div className="mt-2 space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-teal-700">Number of Deliveries:</span>
                  <span className="font-semibold">{customQuantity}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-teal-700">Calculated Price:</span>
                  <span className="font-semibold">{formatPrice(customPrice)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Start Date */}
          <div>
            <Label htmlFor="start-date" className="mb-2 block">
              Delivery Start Date *
            </Label>
            <Input 
              id="start-date"
              type="date" 
              value={startDate}
              onChange={(e) => {
                const selectedDate = e.target.value;
                setStartDate(selectedDate);
              }}
              min={minStartDate}
              required
              className="max-w-xs"
            />
            {/* Warning if selected date is not on a delivery day (never for
                pickup-only plans, which have no delivery days). */}
            {!mealPlan.pickup_only && startDate && (() => {
              const date = new Date(startDate + 'T00:00:00');
              const dayNames = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
              const selectedDayKey = dayNames[date.getDay()];
              const isDeliveryDay = deliveryDays[selectedDayKey] === 1;
              
              if (!isDeliveryDay) {
                const activeDeliveryDays = Object.entries(deliveryDays)
                  .filter(([_, active]) => active === 1)
                  .map(([day]) => day.charAt(0).toUpperCase() + day.slice(1, 3));
                
                return (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <span>⚠️</span>
                    <span>
                      Selected date falls on {selectedDayKey.charAt(0).toUpperCase() + selectedDayKey.slice(1)}, which is not a delivery day. 
                      Please select a date that falls on: {activeDeliveryDays.join(', ')}
                    </span>
                  </p>
                );
              }
              return null;
            })()}
          </div>
          
          {!mealPlan.pickup_only ? (
          <>
          {/* Delivery Days Selection */}
          <div>
            <Label className="mb-2 block">Delivery Days</Label>
            <p className="text-xs text-slate-500 mb-3">
              Select which days you&apos;d like to receive deliveries
            </p>
            <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${visibleDays.length}, minmax(0, 1fr))` }}>
              {visibleDays.map((day) => {
                const active = deliveryDays[day];
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => setDeliveryDays({ ...deliveryDays, [day]: active ? 0 : 1 })}
                    className={`p-3 text-sm font-medium rounded-lg transition-colors ${
                      active
                        ? 'bg-teal-500 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {dayLabels[day]}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {Object.values(deliveryDays).filter(v => v === 1).length} days selected
            </p>
          </div>

          {/* Delivery Instructions */}
          <div>
            <Label htmlFor="delivery-instructions" className="mb-2 block">
              Delivery Instructions (Optional)
            </Label>
            <Textarea
              id="delivery-instructions"
              placeholder="e.g., Leave at front door, Ring doorbell, etc."
              value={deliveryInstructions}
              onChange={(e) => setDeliveryInstructions(e.target.value)}
              rows={3}
            />
          </div>
          </>
          ) : (
          /* v5.11 — pickup-only plan: no delivery days; show a pickup notice
             with the store's pickup address. */
          <div
            data-testid="pickup-only-notice"
            className="rounded-lg border border-teal-200 bg-teal-50 p-4"
          >
            <div className="flex items-start gap-2">
              <MapPin className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-teal-800">Pickup only</p>
                <p className="text-xs text-teal-700 mt-1">
                  This package is available for pickup — no delivery. Your order will be ready to collect at the store.
                </p>
                {pickupAddress && (
                  <p className="text-sm text-teal-800 mt-2 font-medium">
                    {pickupAddress}
                  </p>
                )}
              </div>
            </div>
          </div>
          )}

          {/* Meal Items Preview (if available) */}
          {mealPlan.meal_items && mealPlan.meal_items.length > 0 && (
            <div>
              <Label className="mb-2 block">Includes</Label>
              <div className="bg-slate-50 rounded-lg p-3">
                <ul className="text-sm space-y-2">
                  {mealPlan.meal_items.slice(0, 5).map((item, idx) => {
                    // Use item's custom delivery days, or fall back to meal plan's delivery days
                    const itemDeliveryDays = item.delivery_days || availableDeliveryDays || deliveryDays;
                    const hasDeliveryDays = itemDeliveryDays && Object.keys(itemDeliveryDays).length > 0;
                    
                    return (
                      <li key={idx} className="text-slate-700">
                        <div className="flex items-start justify-between gap-2">
                          <span>
                            • {item.name} {item.quantity && `(${item.quantity} ${item.unit || 'servings'})`}
                          </span>
                          {hasDeliveryDays && (
                            <div className="flex gap-1 flex-wrap">
                              {Object.entries(itemDeliveryDays).map(([day, active]) => 
                                active ? (
                                  <Badge key={day} variant="secondary" className="text-[10px] px-1 py-0">
                                    {dayLabels[day]}
                                  </Badge>
                                ) : null
                              )}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                  {mealPlan.meal_items.length > 5 && (
                    <li className="text-slate-500">+ {mealPlan.meal_items.length - 5} more items</li>
                  )}
                </ul>
              </div>
            </div>
          )}
          
          {/* Composite Meal Plan Sub-Plans */}
          {mealPlan.plan_type === 'composite' && mealPlan.sub_plans && mealPlan.sub_plans.length > 0 && (
            <div>
              <Label className="mb-2 block">Package Contains</Label>
              <div className="bg-slate-50 rounded-lg p-3">
                <ul className="text-sm space-y-2">
                  {mealPlan.sub_plans.map((subPlan, idx) => {
                    // Use sub-plan's custom delivery days, or fall back to meal plan's delivery days
                    const subPlanDeliveryDays = subPlan.delivery_days || availableDeliveryDays || deliveryDays;
                    const hasDeliveryDays = subPlanDeliveryDays && Object.keys(subPlanDeliveryDays).length > 0;
                    
                    return (
                      <li key={idx} className="text-slate-700">
                        <div className="flex items-start justify-between gap-2">
                          <span>
                            • {subPlan.plan_name || subPlan.name} {subPlan.quantity && `(${subPlan.quantity}x)`}
                          </span>
                          {hasDeliveryDays && (
                            <div className="flex gap-1 flex-wrap">
                              {Object.entries(subPlanDeliveryDays).map(([day, active]) => 
                                active ? (
                                  <Badge key={day} variant="secondary" className="text-[10px] px-1 py-0">
                                    {dayLabels[day]}
                                  </Badge>
                                ) : null
                              )}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}

          {/* Free Delivery Promo */}
          {freeDeliveryPromo && !isCustomPlan && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-center" data-testid="free-delivery-promo">
              <p className="text-sm font-medium text-emerald-700">{freeDeliveryPromo}</p>
            </div>
          )}

          {/* Delivery Charge Summary for non-custom orders */}
          {!isCustomPlan && mealPlan.delivery_charge_mode !== 'none' && (() => {
            const qty = useCustomQuantity ? customQuantity : selectedTier.meals;
            const dc = getDeliveryCharge(qty);
            const itemPrice = purchasePrice(useCustomQuantity ? calculateCustomPrice(mealPlan, customQuantity) : selectedTier.price);
            return dc > 0 ? (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1" data-testid="delivery-charge-summary">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Subtotal</span>
                  <span>{formatPrice(itemPrice)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Delivery ({qty} deliveries)</span>
                  <span>{formatPrice(dc)}</span>
                </div>
                <div className="flex justify-between font-semibold border-t pt-1">
                  <span>Total</span>
                  <span className="text-teal-700">{formatPrice(itemPrice + dc)}</span>
                </div>
              </div>
            ) : null;
          })()}

          </>)}

          {/* ─────────── STEP 2: ADD-ONS (optional) ─────────── */}
          {step === 'addons' && (
            <div data-testid="addtocart-addons-step">
              <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Available add-ons</p>
              <p className="text-xs text-slate-500 mb-3">
                Optional. Add extra meals or delivery add-ons — recommended to match your {planQuantity}-delivery plan.
                You can stack up to 3 per delivery day.
              </p>
              <div className="space-y-3 max-h-[42vh] overflow-y-auto pr-1">
                {availableAddons.map((addon) => {
                  const sel = addonSelections[addon.id];
                  const orderable = addonOrderable(addon);
                  const effDays = addonEffectiveDays(addon);
                  const effDayLabels = DAY_KEYS.filter(k => isDayOn(effDays?.[k])).map(k => dayLabels[k]);
                  return (
                    <div key={addon.id} className={`rounded-xl border p-3 transition ${sel ? 'border-teal-400 bg-teal-50/40' : 'border-neutral-200'}`} data-testid={`addon-config-${addon.id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-slate-800 truncate">{addon.name}</p>
                          <p className="text-xs text-slate-500">
                            {addon.is_delivery_addon ? 'Delivery add-on' : 'Meal add-on'}
                            {effDayLabels.length > 0 && effDayLabels.length < 7 && (
                              <span className="ml-1 text-slate-400" data-testid={`addon-days-hint-${addon.id}`}>
                                · delivers {effDayLabels.join(' ')}
                              </span>
                            )}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant={sel ? 'outline' : 'default'}
                          onClick={() => toggleAddon(addon)}
                          disabled={!orderable}
                          className={sel ? 'flex-shrink-0' : 'flex-shrink-0 bg-teal-600 hover:bg-teal-700'}
                          data-testid={`addon-toggle-${addon.id}`}
                        >
                          {sel ? 'Remove' : (orderable ? 'Add' : 'N/A')}
                        </Button>
                      </div>

                      {sel && (
                        <div className="mt-3 space-y-3 border-t pt-3">
                          {/* Delivery-day count — free 1..planQuantity, default = match the plan */}
                          <div>
                            <Label className="text-xs text-slate-600 mb-1.5 block">Delivery days</Label>
                            <div className="flex items-center gap-2">
                              <Button type="button" variant="outline" size="sm" onClick={() => setAddonDays(addon, (sel.days || 1) - 1)} disabled={(sel.days || 1) <= 1} data-testid={`addon-days-dec-${addon.id}`}>−</Button>
                              <span className="w-10 text-center font-medium" data-testid={`addon-days-${addon.id}`}>{sel.days}</span>
                              <Button type="button" variant="outline" size="sm" onClick={() => setAddonDays(addon, (sel.days || 1) + 1)} disabled={(sel.days || 1) >= planQuantity} data-testid={`addon-days-inc-${addon.id}`}>+</Button>
                              <span className="text-xs text-slate-500 ml-1">
                                of {planQuantity} plan deliveries
                                {sel.days === planQuantity && <span className="ml-1 text-[10px] uppercase text-teal-600 font-semibold">matches plan</span>}
                              </span>
                            </div>
                          </div>
                          {/* Per-day multiplier */}
                          <div>
                            <Label className="text-xs text-slate-600 mb-1.5 block">Per delivery day</Label>
                            <div className="flex items-center gap-2">
                              <Button type="button" variant="outline" size="sm" onClick={() => setAddonPerDay(addon, (sel.perDay || 1) - 1)} disabled={(sel.perDay || 1) <= 1} data-testid={`addon-perday-dec-${addon.id}`}>−</Button>
                              <span className="w-10 text-center font-medium" data-testid={`addon-perday-${addon.id}`}>×{sel.perDay || 1}</span>
                              <Button type="button" variant="outline" size="sm" onClick={() => setAddonPerDay(addon, (sel.perDay || 1) + 1)} disabled={(sel.perDay || 1) >= 3} data-testid={`addon-perday-inc-${addon.id}`}>+</Button>
                              <span className="text-xs text-slate-500 ml-1" data-testid={`addon-price-${addon.id}`}>
                                = {sel.days * (sel.perDay || 1)} total · {formatPrice(addonLinePrice(addon, sel))}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ─────────── Footer (step-aware) ─────────── */}
          <div className="flex gap-3 pt-4 border-t">
            {step === 'addons' ? (
              <>
                <Button variant="outline" onClick={() => setStep('plan')} className="flex-1" data-testid="addtocart-back">
                  Back
                </Button>
                <Button
                  onClick={handleFinish}
                  className="flex-1 bg-teal-600 hover:bg-teal-700"
                  data-testid="addtocart-finish"
                >
                  {Object.keys(addonSelections).length > 0
                    ? `Checkout · ${Object.keys(addonSelections).length} add-on${Object.keys(addonSelections).length > 1 ? 's' : ''}`
                    : 'Skip & checkout'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={onClose} className="flex-1">
                  Cancel
                </Button>
                {offerAddons ? (
                  <Button
                    onClick={() => { if (addPlanToCartValid()) setStep('addons'); }}
                    className="flex-1 bg-teal-600 hover:bg-teal-700"
                    disabled={(!isCustomPlan && availableTiers.length === 0) || !startDate}
                    data-testid="addtocart-next"
                  >
                    Next: Add-ons →
                  </Button>
                ) : (
                  <Button
                    onClick={handleAddToCart}
                    className="flex-1 bg-teal-600 hover:bg-teal-700"
                    disabled={(!isCustomPlan && availableTiers.length === 0) || !startDate || (isCustomPlan && customMealItems.length === 0)}
                  >
                    {isCustomPlan ? (
                      `Add Custom Order${customMealItems.length > 0 ? ` (${customMealItems.length} items)` : ''}`
                    ) : (() => {
                      const basePrice = purchasePrice(useCustomQuantity ? calculateCustomPrice(mealPlan, customQuantity) : selectedTier.price);
                      const qty = useCustomQuantity ? customQuantity : selectedTier.meals;
                      const dc = getDeliveryCharge(qty);
                      return `Add to Cart - ${formatPrice(basePrice + dc)}`;
                    })()}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const StorefrontPlanModal = props => props.mealPlan?.storefront_selling?.mode === 'meal_pack'
  ? <MealPackBuilder {...props} subdomain={resolveModalSubdomain(props.subdomain, window.location.search, window.location.hostname)} />
  : <AddToCartModal {...props} />;

export default StorefrontPlanModal;
